export const WEEKLY_REPORT_PUBLIC_SCOPE_UPDATED_EVENT =
  'weekly-report-public-scope-updated';
export const WEEKLY_REPORT_PUBLIC_SCOPE_UPDATED_AT_KEY =
  'weekly-report-public-scope-updated-at';

export type WeeklyReportPublicScopeUpdateDetail = {
  updatedAt: string;
  reason: 'master-setting-saved';
};

export type WeeklyReportPublicScopeRefreshReason =
  | 'focus'
  | 'visibility'
  | 'event'
  | 'storage';

/** 公開範囲の保存成功を、同一画面と同一ブラウザの別タブへ通知する。 */
export const notifyWeeklyReportPublicScopeUpdated =
  (): WeeklyReportPublicScopeUpdateDetail => {
    const detail: WeeklyReportPublicScopeUpdateDetail = {
      updatedAt: new Date().toISOString(),
      reason: 'master-setting-saved',
    };

    if (typeof window === 'undefined') return detail;

    try {
      window.localStorage.setItem(
        WEEKLY_REPORT_PUBLIC_SCOPE_UPDATED_AT_KEY,
        detail.updatedAt
      );
    } catch {
      // localStorage が利用できなくても、同一画面への通知は続行する。
    }

    window.dispatchEvent(
      new CustomEvent<WeeklyReportPublicScopeUpdateDetail>(
        WEEKLY_REPORT_PUBLIC_SCOPE_UPDATED_EVENT,
        { detail }
      )
    );
    return detail;
  };

/**
 * 公開範囲を再検証すべきブラウザイベントを購読する。
 *
 * focus / visibilitychange は「公開範囲が変わった」という確定通知ではないため、
 * 呼び出し側が画面を消さずにバックグラウンド再検証できるよう reason を渡す。
 * CustomEvent / storage は実際の公開範囲更新通知として扱う。
 *
 * focus と visibilitychange は画面復帰時にほぼ同時に発生する場合があるため、
 * 短時間の重複通知をまとめる。
 *
 * 戻り値を effect の cleanup として使用する。
 */
export const subscribeWeeklyReportPublicScopeRefresh = (
  refresh: (reason: WeeklyReportPublicScopeRefreshReason) => void
): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => undefined;
  }

  let lastPassiveRefreshAt = 0;
  const emitPassiveRefresh = (
    reason: Extract<WeeklyReportPublicScopeRefreshReason, 'focus' | 'visibility'>
  ) => {
    if (document.visibilityState === 'hidden') return;

    const now = Date.now();
    if (now - lastPassiveRefreshAt < 500) return;
    lastPassiveRefreshAt = now;
    refresh(reason);
  };

  const handleFocus = () => {
    emitPassiveRefresh('focus');
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      emitPassiveRefresh('visibility');
    }
  };

  const handlePublicScopeUpdated = () => {
    refresh('event');
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key === WEEKLY_REPORT_PUBLIC_SCOPE_UPDATED_AT_KEY) {
      refresh('storage');
    }
  };

  window.addEventListener('focus', handleFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener(
    WEEKLY_REPORT_PUBLIC_SCOPE_UPDATED_EVENT,
    handlePublicScopeUpdated
  );
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener('focus', handleFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener(
      WEEKLY_REPORT_PUBLIC_SCOPE_UPDATED_EVENT,
      handlePublicScopeUpdated
    );
    window.removeEventListener('storage', handleStorage);
  };
};
